import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Segment, Table, Message, Icon, Header, Divider, Grid, Image, Label, Popup } from 'semantic-ui-react';

import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import DemographicsAgeGraph from 'dyzio-shared-components/lib/components/demographics-age-graph';
import DemographicsGenderGraph from 'dyzio-shared-components/lib/components/demographics-gender-graph';
import YoutubeDemographicsCountriesTable from 'dyzio-shared-components/lib/components/youtube-demographics-countries-table';
import DemographicsYoutubeDataTable from 'dyzio-shared-components/lib/components/demographics-youtube-data-table';
import FollowersBarChart from 'dyzio-shared-components/lib/components/followers-bar-chart';
import YoutubeDataTabs from './youtube-data-tabs';
import { commaFormatted } from '../../utils/helpers/formatting';
import hasUserInfo from '../../hocs/has-user-info';


const noBottomPadding = {
  paddingBottom: 0,
};

const chartWrapperStyles = {
  width: '100%',
};

const dividerStyle = {
  marginTop: 5,
  marginBottom: 5,
};

const notificationStyle = {
  paddingTop: 100,
  paddingBottom: 100,
};

const messageStyle = { color: 'grey', fontSize: 16 };

const modalLinkStyle = {
  cursor: 'pointer',
};

const YoutubeProfileDataLarge = props => {
  const {
    toggleYoutubeDemographicsModalState,
    isYouTubeCompliance,
    influencer: {
      countHistory,
      youtube,
      youtubeData,
      name,
      thumbnail,
    },
    youtubeDemographicsModalOpen,
    missingAPILink,
    apiInfluencer,
    userInfo: { organisationId },
    displayAuthorisedData,
  } = props;

  const youtubeDataExists = youtubeData && _.size(youtubeData) > 0;

  const hasYoutubeHandle = _.isString(youtube);
  const lastMonthHistory = _.slice(countHistory, -30);

  const chartProps = {
    rgbColorCode: '#ff0000',
    label: 'subscribers',
    labels: _.map(lastMonthHistory, item => moment(item.createdDate).format('MMM D')),
    values: _.map(lastMonthHistory, 'youtubeCount'),
  };

  let percentageChange;
  if (lastMonthHistory && lastMonthHistory.length > 10) {
    const first = _.head(lastMonthHistory).youtubeCount;
    const last = _.last(lastMonthHistory).youtubeCount;
    percentageChange = ((last - first) / first);
  }


  let avgComments;
  let thirtyDayData;

  if (apiInfluencer) {
    const latestVideos = apiInfluencer.latestYoutubeVideos;
    const sumComments = _.sumBy(latestVideos, lv => parseInt(lv.statistics.commentCount, 10));
    avgComments = (sumComments / latestVideos.length);

    const { channelStatistics } = apiInfluencer;
    const orderedStats = _.orderBy(channelStatistics, 'collectedDate', 'desc');
    const latestStat = _.head(orderedStats);
    const earliestStat = orderedStats[30] || _.last(orderedStats);
    thirtyDayData = {
      subscriberChange: latestStat.subscriberCount - earliestStat.subscriberCount,
      viewChange: latestStat.viewCount - earliestStat.viewCount,
      videoChange: latestStat.videoCount - earliestStat.videoCount,
      dayChange: moment(latestStat.collectedDate).diff(moment(earliestStat.collectedDate), 'days'),
    };
  }

  return (
    <Segment loading={props.loading} color="red">
      <h3>
        { !isYouTubeCompliance && <Icon name="youtube" color="red" /> }
        YouTube
      </h3>
      <Divider />
      <Grid>
        <Grid.Row columns={3} style={noBottomPadding}>
          <Grid.Column width={3}>
            { hasYoutubeHandle && apiInfluencer &&
            <div>
              <Image
                src={thumbnail}
                shape="circular"
                centered
                size="small"
              />

              <Table basic="very" fluid textAlign="center">
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>
                      {apiInfluencer.name}
                      <Icon name="check circle" color="grey" />
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      <Label color="blue" basic content={apiInfluencer.category} />
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      {
                        commaFormatted(apiInfluencer.aggregateYoutubeChannelStatistics.videoCount)
                      } videos
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      {
                        commaFormatted(apiInfluencer.aggregateYoutubeChannelStatistics.subscriberCount)
                      } subscribers
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
            }
          </Grid.Column>


          <Grid.Column width={8}>
            {
              hasYoutubeHandle &&
              <div className="chart-wrapper-print">
                <FollowersBarChart
                  {...chartProps}
                  percentageRange={0.5}
                />
              </div>
            }
            { !hasYoutubeHandle && <div>
              <Header as="h2" icon textAlign="center" style={notificationStyle}>
                <Icon name="minus" color="orange" circular />
                <Header.Content style={messageStyle}>
                  no Youtube channel<br />for this influencer
                </Header.Content>
              </Header>

            </div>
            }
          </Grid.Column>
          <Grid.Column width={5}>
            { hasYoutubeHandle && !props.data && !props.loading && props.error && <Message error>could not load Youtube data</Message>}
            { hasYoutubeHandle && !props.data && !props.loading && missingAPILink && <Message warning>missing api link</Message>}
            { hasYoutubeHandle && apiInfluencer &&
              <YoutubeDataTabs
                percentageChange={percentageChange}
                apiInfluencer={apiInfluencer}
                avgComments={avgComments}
                isYouTubeCompliance={isYouTubeCompliance}
                thirtyDayData={thirtyDayData}
              />
            }
          </Grid.Column>
        </Grid.Row>
        {
          displayAuthorisedData && youtubeDataExists &&
          <Grid.Row textAlign="center" style={noBottomPadding}>
            <Grid.Column width={16}>
              <h2>
                Audience Demographics and Regions (last 30 days)
                <Popup
                  trigger={<Icon name="lock" color="orange" />}
                  content="Private data collected from Youtube Insights API"
                />
              </h2>
            </Grid.Column>
          </Grid.Row>
        }
        {
          displayAuthorisedData && youtubeDataExists &&
            <Grid.Row textAlign="center">
              <Grid.Column width={3}>
                <h5>Viewing by Gender</h5>
                <div className="chart-wrapper-print">
                  <DemographicsGenderGraph {...youtubeData} />
                </div>
                <br />
                <h5>Video View Duration</h5>
                <Table singleLine basic="very" compact>
                  <Table.Row>
                    <Table.Cell>Avg. Watch Time:</Table.Cell>
                    <Table.Cell>
                      {
                        youtubeData && youtubeData.watchTimeMetrics &&
                        <span>
                          {
                            moment
                              .utc(youtubeData.watchTimeMetrics.data.averageViewDuration * 1000)
                              .format('mm[m] ss[s]')
                          }
                        </span>
                      }
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Avg. View Percentage:</Table.Cell>
                    <Table.Cell>
                      {
                        youtubeData && youtubeData.watchTimeMetrics &&
                        <span>
                          {
                            _.round(youtubeData.watchTimeMetrics.data.averageViewPercentage)
                          } %
                        </span>
                      }
                    </Table.Cell>
                  </Table.Row>
                </Table>
              </Grid.Column>
              <Grid.Column width={8}>
                <h5>Channel demographic by gender / age</h5>
                <div className="chart-wrapper-print">
                  <div className="chart-wrapper-print">
                    <DemographicsAgeGraph {...youtubeData} />
                  </div>
                </div>
              </Grid.Column>
              <Grid.Column width={5}>
                <h5>Where are they watching?</h5>
                <div className="chart-wrapper-print padding-top-on-print">
                  <YoutubeDemographicsCountriesTable {...youtubeData} />
                </div>
                <a style={modalLinkStyle} onClick={toggleYoutubeDemographicsModalState}>
                    View full engagement data
                </a>
              </Grid.Column>
            </Grid.Row>
        }
      </Grid>

      {
        youtubeDemographicsModalOpen &&
        <DyzioModal
          headerText="Full Channel Engagement Data (last 30 days)"
          size="fullscreen"
          onClose={toggleYoutubeDemographicsModalState}
        >
          <DemographicsYoutubeDataTable {...youtubeData} />
        </DyzioModal>
      }

    </Segment>
  );
};

YoutubeProfileDataLarge.defaultProps = {
  displayAuthorisedData: false,
};

export default hasUserInfo(YoutubeProfileDataLarge);



/** WEBPACK FOOTER **
 ** ./src/components/youtube-profile-data-large/index.js
 **/